import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getClientsBySeller, getAllClients, deleteClient, updateClient } from '../../Redux/Actions/actions';
import ClientForm from '../ClientForm/ClientForm';
import styles from './ClientList.module.css';

const ClientList = () => {
  const dispatch = useDispatch();
  const { 
    user, 
    userType, 
    clientsBySeller, 
    clients, 
    clientsLoading, 
    clientsError 
  } = useSelector(state => state);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [filter, setFilter] = useState({
    activo: 'true',
    categoria: '',
    search: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const isAdmin = userType === 'administrador';
  const currentClients = isAdmin ? clients : clientsBySeller;

  useEffect(() => {
    // Debug: Verificar estructura del usuario
    console.log('ClientList - user object:', user);
    console.log('ClientList - user.userId:', user?.userId);
    console.log('ClientList - user.user?.id:', user?.user?.id);
    
    // Solo cargar clientes si tenemos el userId disponible
    if (user?.userId || user?.user?.id) {
      loadClients();
    }
  }, [user?.userId, user?.user?.id, isAdmin, filter]);

  const loadClients = () => {
    const params = {
      activo: filter.activo,
      page: currentPage,
      limit: itemsPerPage
    };

    if (filter.categoria) {
      params.categoria = filter.categoria;
    }

    if (isAdmin) {
      dispatch(getAllClients(params));
    } else {
      // Verificar que tengamos un sellerId válido antes de hacer la llamada
      const sellerId = user?.userId || user?.user?.id;
      console.log('Obteniendo clientes para sellerId:', sellerId);
      if (sellerId) {
        dispatch(getClientsBySeller(sellerId, params));
      } else {
        console.warn('No se pudo obtener sellerId para cargar clientes. User object:', user);
      }
    }
  };

  const handleDeleteClient = async (clientId) => {
    if (window.confirm('¿Estás seguro de que deseas desactivar este cliente?')) {
      try {
        // Usar updateClient para cambiar el estado a inactivo
        await dispatch(updateClient(clientId, { activo: false }));
        loadClients(); // Reload the list
      } catch (error) {
        console.error('Error al desactivar cliente:', error);
      }
    }
  };

  const handleActivateClient = async (clientId) => {
    if (window.confirm('¿Deseas reactivar este cliente?')) {
      try {
        // Usar updateClient para cambiar el estado a activo
        await dispatch(updateClient(clientId, { activo: true }));
        loadClients(); // Reload the list
      } catch (error) {
        console.error('Error al reactivar cliente:', error);
      }
    }
  };

  const handleEditClient = (client) => {
    setEditingClient(client);
  };

  const handleFormSuccess = () => {
    setShowCreateForm(false);
    setEditingClient(null);
    loadClients();
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilter(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const filteredClients = currentClients.filter(client => {
    const matchesSearch = !filter.search || 
      `${client.nombre} ${client.apellido} ${client.mail} ${client.telefono}`
        .toLowerCase()
        .includes(filter.search.toLowerCase());
    
    return matchesSearch;
  });

  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedClients = filteredClients.slice(startIndex, startIndex + itemsPerPage);

  // Mostrar loading si no tenemos los datos del usuario aún
  if (!user?.userId && !user?.user?.id) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Cargando información del usuario...</p>
        </div>
      </div>
    );
  }

  if (clientsLoading && currentClients.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Cargando clientes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1>
          {isAdmin ? 'Todos los Clientes' : 'Mis Clientes'} 
          <span className={styles.count}>({filteredClients.length})</span>
        </h1>
        <button 
          className={styles.createButton}
          onClick={() => setShowCreateForm(true)}
        >
          + Crear Cliente
        </button>
      </div>

      {/* Filtros */}
      <div className={styles.filtersContainer}>
        <div className={styles.filterGroup}>
          <label>Buscar:</label>
          <input
            type="text"
            name="search"
            value={filter.search}
            onChange={handleFilterChange}
            placeholder="Nombre, apellido, email o teléfono..."
            className={styles.searchInput}
          />
        </div>

        <div className={styles.filterGroup}>
          <label>Estado:</label>
          <select
            name="activo"
            value={filter.activo}
            onChange={handleFilterChange}
            className={styles.filterSelect}
          >
            <option value="true">Activos</option>
            <option value="false">Inactivos</option>
            {/* <option value="">Todos</option> */}
          </select>
        </div>

        <div className={styles.filterGroup}>
          <label>Categoría:</label>
          <select
            name="categoria"
            value={filter.categoria}
            onChange={handleFilterChange}
            className={styles.filterSelect}
          >
            <option value="">Todas</option>
            <option value="VIP">VIP</option>
            <option value="Premium">Premium</option>
            <option value="Inversor">Inversor</option>
            <option value="Primera compra">Primera compra</option>
            <option value="Regular">Regular</option>
          </select>
        </div>
      </div>

      {clientsError && (
        <div className={styles.errorMessage}>
          {clientsError}
        </div>
      )}

      {/* Lista de clientes */}
      <div className={styles.clientsGrid}>
        {displayedClients.map(client => (
          <div key={client.id} className={styles.clientCard}>
            <div className={styles.clientHeader}>
              <h3>{client.nombre} {client.apellido}</h3>
              <div className={styles.clientActions}>
                <button 
                  onClick={() => handleEditClient(client)}
                  className={styles.editButton}
                  title="Editar cliente"
                >
                  ✏️
                </button>
                {client.activo ? (
                  <button 
                    onClick={() => handleDeleteClient(client.id)}
                    className={styles.deleteButton}
                    title="Desactivar cliente"
                  >
                    🗑️
                  </button>
                ) : (
                  <button 
                    onClick={() => handleActivateClient(client.id)}
                    className={styles.activateButton}
                    title="Reactivar cliente"
                  >
                    🔄
                  </button>
                )}
              </div>
            </div>
            
            <div className={styles.clientInfo}>
              <div className={styles.infoItem}>
                <span className={styles.label}>📧 Email:</span>
                <span>{client.mail}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.label}>📞 Teléfono:</span>
                <span>{client.telefono}</span>
              </div>
              {client.categoria && (
                <div className={styles.infoItem}>
                  <span className={styles.label}>🏷️ Categoría:</span>
                  <span className={styles.categoria}>{client.categoria}</span>
                </div>
              )}
              {client.detalle && (
                <div className={styles.infoItem}>
                  <span className={styles.label}>📝 Detalle:</span>
                  <span className={styles.detalle}>{client.detalle}</span>
                </div>
              )}
              {isAdmin && client.Seller && (
                <div className={styles.infoItem}>
                  <span className={styles.label}>👤 Seller:</span>
                  <span>{client.Seller.nombre} {client.Seller.apellido}</span>
                </div>
              )}
            </div>

            <div className={styles.clientFooter}>
              <span 
                className={`${styles.status} ${client.activo ? styles.active : styles.inactive}`}
              >
                {client.activo ? 'Activo' : 'Inactivo'}
              </span>
              <span className={styles.createdDate}>
                Creado: {new Date(client.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        ))}
      </div>

      {filteredClients.length === 0 && !clientsLoading && (
        <div className={styles.emptyState}>
          <h3>No hay clientes</h3>
          <p>
            {filter.search || filter.categoria 
              ? 'No se encontraron clientes que coincidan con los filtros.' 
              : 'Aún no has creado ningún cliente.'
            }
          </p>
          <button 
            className={styles.createButton}
            onClick={() => setShowCreateForm(true)}
          >
            Crear mi primer cliente
          </button>
        </div>
      )}

      {/* Paginación */}
      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={styles.pageButton}
          >
            Anterior
          </button>
          
          <span className={styles.pageInfo}>
            Página {currentPage} de {totalPages}
          </span>
          
          <button 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className={styles.pageButton}
          >
            Siguiente
          </button>
        </div>
      )}

      {/* Modal de creación */}
      {showCreateForm && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <ClientForm 
              onSuccess={handleFormSuccess}
              onCancel={() => setShowCreateForm(false)}
            />
          </div>
        </div>
      )}

      {/* Modal de edición */}
      {editingClient && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <ClientForm 
              client={editingClient}
              onSuccess={handleFormSuccess}
              onCancel={() => setEditingClient(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientList;